import { ReactElement, useRef, useState } from 'react'
import dayjs from 'dayjs'
import { useForm } from '@tanstack/react-form'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

import axios, { pluckData, pluckError } from 'src/utilities/http-client'
import {
  CancelIcon,
  SubmitIcon,
  UploadFileIcon,
  UploadIcon,
} from 'src/v3/core/icons'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'

import {
  fetchLicenses,
  fetchLicenseVerifications,
  fetchUnstartedComplianceLicenseItems,
} from 'src/v3/profile/profile.slice'
import { selectUseCompliance } from 'src/v3/settings/settings.slice'
import { DisplayRank, License } from 'src/v3/core/types'
import LicenseTypeDropdown from 'src/v3/components/form-fields/license-type-dropdown'
import StateDropdown from 'src/v3/components/form-fields/state-dropdown'
import { FileData, handleImageCapture } from 'src/v3/utilities/filesystem'

let licenseTypeValues = [
  'AEMT',
  'Advanced Care Paramedic (Canada Only)',
  'CFR',
  'EMR',
  'EMT',
  'EMT-1',
  'EMT-2',
  'EMT-B',
  'EMT-CC',
  'EMT-D',
  'Paramedic',
  'Physician',
  'Physician Assistant',
  'Primary Care Paramedic (Canada Only)',
  'Registered Nurse',
  'Other',
]

let normalizeType = (type?: string) =>
  type
    ? (licenseTypeValues.find((v) => v.toLowerCase() === type.toLowerCase()) ??
      type)
    : 'unselected'

type LicenseFormProps = {
  license?: License
  displayRank?: DisplayRank
  initialState?: string
  initialType?: string
  open: boolean
  setOpen: (status: boolean) => void
  onError: (error: string) => void
}

export let LicenseForm = ({
  license,
  displayRank,
  initialState,
  initialType,
  open,
  setOpen,
  onError,
}: LicenseFormProps): ReactElement => {
  let dispatch = useAppDispatch()
  let useCompliance = useAppSelector(selectUseCompliance)

  let [image, setImage] = useState<FileData | undefined>(undefined)
  let [saving, setSaving] = useState(false)
  let fileInputRef = useRef<HTMLInputElement>(null)

  let fromCompliance = !!initialType

  let form = useForm({
    defaultValues: {
      number: license?.number ?? '',
      state:
        !!license?.state && license.state !== 'Other' && license.state !== ''
          ? license.state
          : (initialState ?? 'unselected'),
      type:
        !!license?.type && license.type !== ''
          ? license.type
          : normalizeType(initialType),
      issueDate: license?.issueDate ?? '',
      expiration: license?.expiration ?? '',
    },
    onSubmit: async ({ value, formApi }) => {
      if (fromCompliance && !image) return
      let request = !license ? axios.post : axios.put
      setSaving(true)
      request('/api/profile/license', {
        id: license?.id,
        number: value.number,
        state: value.state,
        type: value.type,
        issueDate: value.issueDate || undefined,
        expiration: value.expiration,
        displayRank,
      })
        .then(pluckData)
        .then((data) => {
          if (!license && useCompliance) {
            axios
              .post('/api/profile/compliance/link-license', { id: data.id })
              .then(() => dispatch(fetchUnstartedComplianceLicenseItems()))
              .catch((err) =>
                onError?.(
                  pluckError(err, 'Error linking license to compliance'),
                ),
              )
          }
          if (image) {
            axios
              .put<string>('/api/profile/license/image', {
                id: license?.id || data.id,
                imageType:
                  image.extension === 'jpeg' || image.extension === 'jpg'
                    ? 'jpg'
                    : image.extension === 'png'
                      ? 'png'
                      : 'pdf',
              })
              .then(pluckData)
              .then((response) =>
                axios.put(response, image?.file, {
                  headers: {
                    'Cache-Control': 'max-age=2592000',
                    'content-type': image?.type,
                  },
                }),
              )
          }
        })
        .then(() => dispatch(fetchLicenses()))
        .then(() => dispatch(fetchLicenseVerifications()))
        .then(() => setOpen(false))
        .then(() => formApi.reset())
        .catch((err) => {
          onError?.(pluckError(err, 'Error Updating License'))
        })
        .finally(() => setSaving(false))
    },
  })

  let handleOnClose = () => {
    form.reset()
    setOpen(false)
  }

  return (
    <Dialog open={open} onClose={handleOnClose}>
      <Paper sx={{ width: 300, px: 2, py: 1 }}>
        <DialogTitle sx={{ textAlign: 'center', mb: -3 }}>
          {!license ? 'Add' : 'Edit'} License
        </DialogTitle>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <DialogContent
            sx={{
              px: 2,
              pb: 1,
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
            }}
          >
            <form.Field
              name="type"
              validators={{
                onChange: ({ value }) =>
                  value === 'unselected'
                    ? 'License Type is a required field'
                    : undefined,
              }}
              children={(field) => (
                <LicenseTypeDropdown
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={fromCompliance}
                  error={field.state.meta.errors.length > 0}
                  errorText={field.state.meta.errors.join(', ')}
                  variant="outlined"
                  bgDefaultColor="background.paper"
                />
              )}
            />
            <form.Field
              name="state"
              validators={{
                onChange: ({ value }) =>
                  value === 'unselected'
                    ? 'License State is a required field'
                    : undefined,
              }}
              children={(field) => (
                <StateDropdown
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  disabled={fromCompliance}
                  error={field.state.meta.errors.length > 0}
                  errorText={field.state.meta.errors.join(', ')}
                  variant="outlined"
                  bgDefaultColor="background.paper"
                />
              )}
            />
            <form.Field
              name="number"
              validators={{
                onChange: ({ value }) =>
                  !value ? 'License Number is a required field.' : undefined,
              }}
              children={(field) => (
                <TextField
                  fullWidth
                  variant="outlined"
                  sx={{ bgcolor: 'background.paper' }}
                  value={field.state.value}
                  label="Number"
                  name={field.name}
                  onChange={(e) => field.handleChange(e.target.value)}
                  onBlur={field.handleBlur}
                  error={field.state.meta.errors.length > 0}
                  helperText={field.state.meta.errors.join(', ')}
                />
              )}
            />
            <form.Field
              name="issueDate"
              children={(field) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Issue Date"
                    name={field.name}
                    value={dayjs(field.state.value)}
                    onChange={(value) =>
                      field.handleChange(
                        !value ? '' : value.format('MM/DD/YYYY'),
                      )
                    }
                    slotProps={{
                      textField: {
                        sx: { bgcolor: 'background.paper' },
                        error: field.state.meta.errors.length > 0,
                        helperText: field.state.meta.errors.join(', '),
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
            <form.Field
              name="expiration"
              validators={{
                onChange: ({ value }) =>
                  !value ? 'License Expiration is a required field' : undefined,
              }}
              children={(field) => (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Expiration"
                    name={field.name}
                    value={dayjs(field.state.value)}
                    onChange={(value) =>
                      field.handleChange(
                        !value ? '' : value.format('MM/DD/YYYY'),
                      )
                    }
                    slotProps={{
                      textField: {
                        sx: { bgcolor: 'background.paper' },
                        error: field.state.meta.errors.length > 0,
                        helperText: field.state.meta.errors.join(', '),
                      },
                    }}
                  />
                </LocalizationProvider>
              )}
            />
            <Box>
              <input
                ref={fileInputRef}
                accept="image/*,.pdf"
                style={{ display: 'none' }}
                type="file"
                onChange={(e) => handleImageCapture(e, setImage, onError)}
              />
              <Button
                type="button"
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => fileInputRef.current?.click()}
              >
                {!license || !license?.imageName ? 'Select' : 'Overwrite'} File
              </Button>
              {image && (
                <Typography variant="subtitle1">
                  <UploadFileIcon
                    color="primary"
                    sx={{
                      mr: '2px',
                      mb: '2px',
                    }}
                  />
                  {image.name}
                </Typography>
              )}
              {fromCompliance && !image && (
                <Typography color="error" variant="subtitle2">
                  *A file is required to satisfy your manager's requirement.
                </Typography>
              )}
              {license?.imageName && (
                <Typography color="text.secondary" variant="subtitle2">
                  *The existing license file will only be overwritten upon
                  submitting this form with a new image uploaded.
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions
            sx={{
              display: 'flex',
              justifyContent: 'center',
              pb: 1.5,
            }}
          >
            <form.Subscribe
              selector={(state) => state.isValid}
              children={(isValid) => (
                <Button
                  color="primary"
                  type="submit"
                  variant="contained"
                  sx={{
                    flex: '1 1 0px',
                  }}
                  startIcon={<SubmitIcon />}
                  disabled={!isValid || (fromCompliance && !image)}
                  loading={saving}
                >
                  Submit
                </Button>
              )}
            />
            <Button
              color="error"
              onClick={handleOnClose}
              variant="contained"
              sx={{
                flex: '1 1 0px',
              }}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
      </Paper>
    </Dialog>
  )
}
